/**
 * Node visual para Formatar Array
 * Transforma um array em texto formatado usando template
 * Inclui toolbar flutuante ao selecionar
 * 
 * RELEASE: 1.0.0 - 2024-12-24 - Novo node para formatar arrays em mensagens
 * RELEASE: 2.0.0 - 2024-12-25 - Indicador visual para modo envio de documentos
 * RELEASE: 3.0.0 - 2024-12-25 - Indicadores visuais para limite e ordenação
 */

import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { FileText, FileDown, SortAsc, SortDesc } from 'lucide-react';
import { FlowNodeToolbar } from './FlowNodeToolbar';

interface FormatArrayNodeProps {
  data: {
    type: string;
    label: string;
    config?: {
      source_variable?: string;
      output_variable?: string;
      item_template?: string;
      separator?: string;
      filter_field?: string;
      filter_value?: string;
      header_template?: string;
      footer_template?: string;
      empty_message?: string;
      // Campos para envio de documentos
      send_documents?: boolean;
      document_url_field?: string;
      document_caption_template?: string;
      document_filename_template?: string;
      // NOVOS: Campos para limite e ordenação
      limit_items?: number;
      sort_field?: string;
      sort_direction?: 'asc' | 'desc';
    };
    onEdit?: () => void;
    onDelete?: () => void;
  };
  id: string;
  selected?: boolean;
}

/**
 * Componente visual do node Format Array
 * Exibe configuração de formatação de array no canvas
 */
export const FormatArrayNode = memo(({ data, id, selected }: FormatArrayNodeProps) => {
  const config = data.config || {};
  const sourceVariable = config.source_variable || '';
  const outputVariable = config.output_variable || '';
  const itemTemplate = config.item_template || '';
  const filterField = config.filter_field || '';
  const filterValue = config.filter_value || '';
  const limitItems = config.limit_items || 0;
  const sortField = config.sort_field || '';
  const sortDirection = config.sort_direction || 'asc';

  // Callbacks injetados via FlowCanvas
  const onEdit = data.onEdit;
  const onDuplicate = (data as any).onDuplicate;
  const onDelete = data.onDelete;
  const onTestFromHere = (data as any).onTestFromHere;

  // Verifica se está configurado
  const isConfigured = sourceVariable && outputVariable && itemTemplate;
  const hasFilter = filterField && filterValue;
  const sendDocuments = config.send_documents || false;
  const hasLimit = limitItems > 0;
  const hasSort = !!sortField;

  // Preview do template (primeiras linhas)
  const templatePreview = itemTemplate.length > 50 
    ? itemTemplate.substring(0, 50) + '...' 
    : itemTemplate;

  return (
    <div className="px-4 py-3 shadow-lg rounded-lg border-2 border-emerald-500 bg-card min-w-[240px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={data.type || 'format_array'}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-emerald-500" />
      
      <div className="flex items-start gap-2">
        <div className={`flex items-center justify-center w-8 h-8 rounded-lg text-white flex-shrink-0 ${sendDocuments ? 'bg-blue-500' : 'bg-emerald-500'}`}>
          {sendDocuments ? <FileDown className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-1 flex items-center gap-2 flex-wrap">
            {(data.config as any)?.nickname || 'Formatar Array'}
            {sendDocuments && (
              <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded font-medium">
                PDF
              </span>
            )}
            {hasLimit && (
              <span className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-600 dark:text-orange-400 rounded font-medium">
                Limite: {limitItems}
              </span>
            )}
          </div>
          
          {isConfigured ? (
            <div className="text-xs space-y-1">
              {/* Entrada */}
              <div className="font-mono bg-muted px-2 py-1 rounded">
                <span className="text-muted-foreground">De: </span>
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                  {sourceVariable}
                </span>
              </div>
              
              {/* Ordenação (se configurada) */}
              {hasSort && (
                <div className="font-mono bg-muted px-2 py-1 rounded flex items-center gap-1">
                  {sortDirection === 'asc' ? (
                    <SortAsc className="h-3 w-3 text-orange-500" />
                  ) : (
                    <SortDesc className="h-3 w-3 text-orange-500" />
                  )}
                  <span className="text-muted-foreground">Ordenar: </span>
                  <span className="text-orange-600 dark:text-orange-400">{sortField}</span>
                  <span className="text-muted-foreground">
                    ({sortDirection === 'asc' ? '↑' : '↓'})
                  </span>
                </div>
              )}
              
              {/* Filtro (se configurado) */}
              {hasFilter && (
                <div className="font-mono bg-muted px-2 py-1 rounded">
                  <span className="text-muted-foreground">Filtro: </span>
                  <span className="text-blue-600 dark:text-blue-400">{filterField}</span>
                  {' = '}
                  <span className="text-green-600 dark:text-green-400">"{filterValue}"</span>
                </div>
              )}
              
              {/* Template preview */}
              <div className="font-mono bg-muted px-2 py-1 rounded text-muted-foreground italic truncate">
                {templatePreview}
              </div>
              
              {/* Saída */}
              <div className="font-mono bg-muted px-2 py-1 rounded">
                <span className="text-muted-foreground">Salvar em: </span>
                <span className="text-cyan-600 dark:text-cyan-400 font-semibold">
                  {outputVariable}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">
              Clique para configurar
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-emerald-500" />
    </div>
  );
});

FormatArrayNode.displayName = 'FormatArrayNode';
